import React from 'react';

/**
 * Format-Leiste fuer den Markdown-Editor: fuegt Markdown an der Auswahl/Cursor-
 * position ein (dependency-frei ueber das textarea-Ref). Der Editor bleibt
 * kontrolliert – Aenderungen laufen ueber `onChange`, die Auswahl wird danach
 * wiederhergestellt.
 */
export default function EditorToolbar({ textareaRef, value, onChange }) {
  // Fuehrt eine Transformationsfunktion aus und stellt Fokus/Auswahl wieder her.
  const wende = (fn) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const { text, selStart, selEnd } = fn(value ?? '', ta.selectionStart, ta.selectionEnd);
    onChange(text);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(selStart, selEnd);
    });
  };

  const umschliesseAuswahl = (vor, nach) => (text, start, end) => {
    const auswahl = text.slice(start, end);
    const neu = text.slice(0, start) + vor + auswahl + nach + text.slice(end);
    const s = start + vor.length;
    return { text: neu, selStart: s, selEnd: s + auswahl.length };
  };

  const zeilenPrefix = (prefix) => (text, start, end) => {
    const zeilenStart = text.lastIndexOf('\n', start - 1) + 1;
    const bereich = text.slice(zeilenStart, end);
    const ersetzt = bereich.split('\n').map((z) => prefix + z).join('\n');
    const neu = text.slice(0, zeilenStart) + ersetzt + text.slice(end);
    return { text: neu, selStart: zeilenStart, selEnd: zeilenStart + ersetzt.length };
  };

  const einfuegeBlock = (block) => (text, start) => {
    const davor = start > 0 && text[start - 1] !== '\n' ? '\n' : '';
    const eingefuegt = davor + block;
    const neu = text.slice(0, start) + eingefuegt + text.slice(start);
    const pos = start + eingefuegt.length;
    return { text: neu, selStart: pos, selEnd: pos };
  };

  const TABELLE = '| Spalte 1 | Spalte 2 |\n| --- | --- |\n| Wert | Wert |\n';

  const gruppen = [
    [
      { label: 'B', title: 'Fett', klasse: 'font-bold', fn: umschliesseAuswahl('**', '**') },
      { label: 'I', title: 'Kursiv', klasse: 'italic', fn: umschliesseAuswahl('*', '*') },
      { label: 'S', title: 'Durchgestrichen', klasse: 'line-through', fn: umschliesseAuswahl('~~', '~~') },
      { label: '</>', title: 'Inline-Code', klasse: 'font-mono text-xs', fn: umschliesseAuswahl('`', '`') },
    ],
    [
      { label: 'H1', title: 'Überschrift 1', fn: zeilenPrefix('# ') },
      { label: 'H2', title: 'Überschrift 2', fn: zeilenPrefix('## ') },
      { label: 'H3', title: 'Überschrift 3', fn: zeilenPrefix('### ') },
    ],
    [
      { label: '•', title: 'Liste', fn: zeilenPrefix('- ') },
      { label: '1.', title: 'Nummerierte Liste', fn: zeilenPrefix('1. ') },
      { label: '☑', title: 'Checkliste', fn: zeilenPrefix('- [ ] ') },
      { label: '❝', title: 'Zitat', fn: zeilenPrefix('> ') },
    ],
    [
      { label: '🔗', title: 'Link', fn: umschliesseAuswahl('[', '](https://)') },
      { label: '{ }', title: 'Codeblock', klasse: 'font-mono text-xs', fn: einfuegeBlock('```\n\n```\n') },
      { label: '―', title: 'Trennlinie', fn: einfuegeBlock('---\n') },
      { label: '▦', title: 'Tabelle', fn: einfuegeBlock(TABELLE) },
    ],
  ];

  return (
    <div className="flex flex-wrap items-center gap-1 rounded-t-lg border border-b-0 border-line bg-paper-soft px-2 py-1.5">
      {gruppen.map((gruppe, gi) => (
        <React.Fragment key={gi}>
          {gi > 0 && <span className="mx-1 h-5 w-px bg-line" aria-hidden="true" />}
          {gruppe.map((b) => (
            <button
              key={b.title}
              type="button"
              title={b.title}
              onClick={() => wende(b.fn)}
              className={`flex h-7 min-w-[1.75rem] items-center justify-center rounded px-1.5 text-sm text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink ${b.klasse || ''}`}
            >
              {b.label}
            </button>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}
